import React from 'react';

import { bffQuery } from '../lib/bffClient';
import { useOptionalClinicContext } from '../context/ClinicContext.jsx';

/**
 * What the ACTIVE clinic's COUNTRY allows, and the defaults it implies — the
 * clinic BFF's `myClinic.capabilities` (PM-0218, docs/country-config/CONTRACT.md
 * §4 in the monorepo): its e-prescription provider, messaging channels, payment
 * rails and bill services, payroll profile and insurer catalogue, and its
 * language, calendar, week, currency, zone, national-id rule, dial code, address
 * model and map.
 *
 * Not to be confused with useSubscription's `useClinicCapabilities` — that one is
 * the subscription's entitlement snapshot (what the clinic PAID for); this one is
 * what its jurisdiction allows.
 *
 * Returns the object, or `null` while it is unknown: before the first answer, on
 * any error, and with no active clinic. `null` means "behave as before" to every
 * caller, which is why every failure lands on it rather than on a guess.
 *
 * Asked in its OWN document, never as a field added to another query: a panel
 * newer than its BFF must lose only this answer (the field does not exist there
 * yet), never `myClinic` itself.
 *
 * One answer per clinic, kept for five minutes and shared by every screen that
 * asks; a failed read is forgotten, so the next mount asks again.
 */

export const CLINIC_CAPABILITIES_QUERY = `
  query ClinicCapabilities {
    myClinic {
      id
      country
      capabilities {
        country erx councilRegistry smsProvider messagingChannels paymentRails billServices
        payrollProfile insuranceCatalog defaultLanguage calendar weekStart weekendDays
        defaultCurrency defaultTimezone nationalIdRule nationalDialCode addressModel
        mapView { lat lon zoom }
        printCalendar printLanguage
      }
    }
  }
`;

const TTL_MS = 5 * 60 * 1000;

// clinicId → { promise, value, at } — `value` once answered.
const answers = new Map();

function load(clinicId) {
  const hit = answers.get(clinicId);
  if (hit && (!('value' in hit) || Date.now() - hit.at < TTL_MS)) return hit.promise;
  const entry = {};
  entry.promise = bffQuery(CLINIC_CAPABILITIES_QUERY, {}, 'ClinicCapabilities')
    .then((data) => {
      const clinic = data?.myClinic;
      // The answer must be for the clinic it was asked for: a switch while it was
      // in flight is not this clinic's.
      const value = clinic?.id === clinicId && clinic.capabilities && typeof clinic.capabilities === 'object'
        ? clinic.capabilities
        : null;
      entry.value = value;
      entry.at = Date.now();
      if (!value) answers.delete(clinicId);
      return value;
    })
    .catch(() => {
      answers.delete(clinicId);
      return null;
    });
  answers.set(clinicId, entry);
  return entry.promise;
}

/** Tests only: forget every answer. */
export function resetClinicCapabilities() {
  answers.clear();
}

// What is already known about a clinic, synchronously — so a screen mounted after
// the first answer, or a switch back to a clinic already asked about, starts on
// the answer rather than on null.
function known(clinicId) {
  const hit = clinicId ? answers.get(clinicId) : null;
  return hit && 'value' in hit ? hit.value : null;
}

/** The active clinic's `Capabilities`, or null while unknown (= behave as before). */
export function useClinicCapabilities() {
  const clinic = useOptionalClinicContext();
  const clinicId = clinic?.activeMembership?.clinicId || '';
  const [state, setState] = React.useState(() => ({ clinicId, value: known(clinicId) }));

  React.useEffect(() => {
    if (!clinicId) return undefined;
    let live = true;
    load(clinicId).then((value) => {
      if (live) setState((prev) => (prev.clinicId === clinicId && prev.value === value ? prev : { clinicId, value }));
    });
    return () => { live = false; };
  }, [clinicId]);

  return state.clinicId === clinicId ? state.value : known(clinicId);
}

export default useClinicCapabilities;
